import * as pdfjsModule from 'pdfjs-dist/webpack';
import { confirmRemunerationScan, identifyDocument, PdfTextItem, DocumentReadResult } from './identifyDocument';
import { readScanText } from './ocrScanPage';

const pdfjs = (
  typeof pdfjsModule.getDocument === 'function'
    ? pdfjsModule
    : (pdfjsModule as unknown as { default: typeof pdfjsModule }).default
) as typeof pdfjsModule;

interface RenderedPage {
  getViewport: (params: { scale: number }) => { width: number; height: number };
  render: (params: {
    canvasContext: CanvasRenderingContext2D;
    viewport: { width: number; height: number };
  }) => { promise: Promise<void> };
}

interface OpenPdf {
  numPages: number;
  getPage: (pageNumber: number) => Promise<RenderedPage & {
    getTextContent: () => Promise<{ items: unknown[] }>;
  }>;
  destroy: () => Promise<void> | void;
}

/**
 * Reads text already stored in the PDF. A scan is accepted as
 * 基準報酬決定通知書 only after the page heading is read locally.
 */
export async function readDocumentPdf(file: File): Promise<DocumentReadResult> {
  const data = new Uint8Array(await file.arrayBuffer());
  const document = await pdfjs.getDocument({ data }).promise as unknown as OpenPdf;
  try {
    const items = await extractText(document);
    const preliminary = identifyDocument(items, file.name);
    if (preliminary.status !== 'image-only') return preliminary;
    const ocrText = await readScanText(await renderFirstPage(document));
    return confirmRemunerationScan(ocrText, file.name);
  } finally {
    await document.destroy();
  }
}

async function extractText(document: OpenPdf): Promise<PdfTextItem[]> {
  const items: PdfTextItem[] = [];
  for (let pageNumber = 1; pageNumber <= document.numPages; pageNumber += 1) {
    const page = await document.getPage(pageNumber);
    const content = await page.getTextContent();
    content.items.forEach((item) => {
      if (typeof item !== 'object' || item === null || !('str' in item)) return;
      const textItem = item as { str?: string; transform?: number[] };
      if (!textItem.str?.trim() || !textItem.transform) return;
      items.push({
        str: textItem.str,
        x: textItem.transform[4],
        y: textItem.transform[5] - (pageNumber - 1) * 2000,
      });
    });
  }
  return items;
}

async function renderFirstPage(document: OpenPdf): Promise<HTMLCanvasElement> {
  const page = await document.getPage(1);
  const base = page.getViewport({ scale: 1 });
  const scale = Math.min(2, 1800 / base.width);
  const viewport = page.getViewport({ scale });
  const canvas = window.document.createElement('canvas');
  canvas.width = Math.ceil(viewport.width);
  canvas.height = Math.ceil(viewport.height);
  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('ページ画像を用意できませんでした。');
  }
  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, canvas.width, canvas.height);
  await page.render({ canvasContext: context, viewport }).promise;
  return canvas;
}
